import { useEffect, useRef } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { draftSaveIndicator, type DraftSaveSnapshot } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useWebsiteT } from '../hooks/use-website-t';

export interface DraftSaveStatusChipProps {
  readonly snapshot: DraftSaveSnapshot;
  /** `toolbar`: a compact status next to Publish. `dialog`: one line in the region footer. */
  readonly variant: 'toolbar' | 'dialog';
  /** PRD §12.3 row 3: the disabled "All changes published" CTA carries the message instead. */
  readonly hidden?: boolean;
  /**
   * Whether status changes are announced. The toolbar turns it off while a
   * region dialog (with its own status) is open, so nothing is read twice.
   */
  readonly live?: boolean;
  readonly onRetry: () => void;
  /** Reload of a conflict; without it the conflict text stands alone (the dialog's banner owns Reload). */
  readonly onReload?: () => void;
  /** "Go to item" of a kept server-only rejection; without it the text stands alone. */
  readonly onGoToItem?: (slotId: string) => void;
}

const KEYS = 'admin.websiteTemplates.editor.save';

/**
 * Epic #228 P4 (PD12): the save status, read from the queue snapshot only.
 * "Saving…" while anything is unconfirmed; "All changes saved" only after
 * the server answered; failures carry their one action. The text is the
 * live region; the action is a real button outside it.
 *
 * Fix round 2: in the toolbar a saved status is a check and "Saved" and a
 * conflict is a short label, each with the full sentence as its accessible
 * text, so Publish keeps its place on the row. When the action button goes
 * away after it was used (Retry, Reload), focus moves to the status instead
 * of falling to the page.
 */
export function DraftSaveStatusChip({
  snapshot,
  variant,
  hidden = false,
  live = true,
  onRetry,
  onReload,
  onGoToItem,
}: DraftSaveStatusChipProps): React.JSX.Element | null {
  const { t } = useWebsiteT();
  const containerRef = useRef<HTMLDivElement>(null);
  const actionUsedRef = useRef(false);
  const indicator = draftSaveIndicator(snapshot);
  const rejectedSlotId = Object.keys(snapshot.rejected)[0];
  const toolbar = variant === 'toolbar';

  let action: { readonly key: string; readonly testId: string; readonly run: () => void } | null =
    null;
  if (!hidden && indicator === 'failed') {
    action = { key: `${KEYS}.retry`, testId: 'draft-save-retry', run: onRetry };
  } else if (!hidden && indicator === 'conflict' && onReload) {
    action = { key: `${KEYS}.reload`, testId: 'draft-save-reload', run: onReload };
  } else if (!hidden && indicator === 'rejected' && onGoToItem && rejectedSlotId !== undefined) {
    action = {
      key: 'admin.websiteTemplates.readiness.goTo',
      testId: 'draft-save-go-to-item',
      run: () => onGoToItem(rejectedSlotId),
    };
  }
  const current = action;
  const hasAction = current !== null;

  // F1: the button the merchant pressed unmounts while the queue works; keep
  // keyboard focus on the status (the live region says what happened next).
  useEffect(() => {
    if (hasAction || !actionUsedRef.current) return;
    actionUsedRef.current = false;
    const active = document.activeElement;
    if (active === null || active === document.body) {
      containerRef.current?.focus({ preventScroll: true });
    }
  }, [hasAction]);

  const ariaLive = live ? 'polite' : 'off';
  if (hidden) {
    // The same element tree as below, so the live region node survives and
    // the next status is announced.
    return (
      <div ref={containerRef} tabIndex={-1} className="sr-only">
        <span role="status" aria-live={ariaLive} />
      </div>
    );
  }

  const trouble = indicator === 'failed' || indicator === 'conflict' || indicator === 'rejected';
  const message =
    indicator === 'conflict'
      ? `${t(`${KEYS}.conflict`)} ${t(`${KEYS}.conflictHelp`)}`
      : t(`${KEYS}.${indicator}`);
  // The toolbar shows short visible labels; the full sentence stays the
  // accessible text and the tooltip.
  const shortLabel = !toolbar
    ? null
    : indicator === 'conflict'
      ? t(`${KEYS}.conflictShort`)
      : indicator === 'saved'
        ? t(`${KEYS}.savedShort`)
        : null;

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      data-testid={toolbar ? 'draft-save-status' : 'region-save-status'}
      data-indicator={indicator}
      title={shortLabel === null ? undefined : message}
      className={cn(
        'flex min-w-0 max-w-full items-center gap-2 text-sm outline-none',
        'focus-visible:ring-ring rounded-full focus-visible:ring-2',
        // Only a problem gets the filled pill; saving and saved stay quiet text.
        toolbar && trouble && 'bg-admin-surface-muted py-1 pe-1 ps-3',
        toolbar && trouble && current === null && 'pe-3',
      )}
    >
      <span
        role="status"
        aria-live={ariaLive}
        className={cn(
          'flex min-w-0 items-center gap-1.5 text-start',
          trouble ? 'text-destructive' : 'text-muted-foreground',
          // Wrap rather than overflow when a message is longer than the row.
          toolbar && 'break-words',
        )}
      >
        {indicator === 'saving' ? (
          <Loader2 aria-hidden="true" className="size-4 shrink-0 motion-safe:animate-spin" />
        ) : null}
        {toolbar && indicator === 'saved' ? (
          <Check aria-hidden="true" className="size-4 shrink-0" />
        ) : null}
        {shortLabel === null ? (
          message
        ) : (
          <>
            <span aria-hidden="true">{shortLabel}</span>
            <span className="sr-only">{message}</span>
          </>
        )}
      </span>
      {current === null ? null : (
        <Button
          type="button"
          variant={toolbar ? 'ghost' : 'outline'}
          size="sm"
          className="min-h-11 shrink-0"
          data-testid={current.testId}
          onClick={() => {
            actionUsedRef.current = true;
            current.run();
          }}
        >
          {t(current.key)}
        </Button>
      )}
    </div>
  );
}
